/**
 * Sidebar Navigation Component
 * Modern sidebar with icons and labels, collapses to bottom nav on mobile
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/admin';

function Sidebar({ activeTab, onTabChange, serverStatus }) {
  const { user, signOut } = useAuth();
  const [avatarError, setAvatarError] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Navigation items
  const navItems = [
    { id: 'upload', label: 'Upload Receipt', icon: '📸', section: 'main' },
    { id: 'statement', label: 'Bank Statement', icon: '🏦', section: 'main' },
    { id: 'manual', label: 'Add Expense', icon: '➕', section: 'main' },
    { id: 'expenses', label: 'Expenses', icon: '💰', section: 'main' },
    { id: 'receipts', label: 'Receipts', icon: '🧾', section: 'main' },
    { id: 'email-forwarding', label: 'Email Forward', icon: '📧', section: 'main' },
    { id: 'analytics', label: 'Analytics', icon: '📊', section: 'main' },
    { id: 'readme', label: 'Readme', icon: '📖', section: 'main' },
    { id: 'ask', label: 'Ask AI', icon: '🤖', section: 'tools' },
    { id: 'currency', label: 'Currency Rates', icon: '💱', section: 'tools' },
    { id: 'feedback', label: 'Submit Feedback', icon: '💬', section: 'tools' },
    { id: 'settings', label: 'Settings', icon: '⚙️', section: 'settings' },
    { id: 'telegram', label: 'Telegram', icon: '✈️', section: 'settings' },
    { id: 'privacy', label: 'Privacy', icon: '🔒', section: 'settings' },
    ...(isAdmin(user) ? [
      { id: 'inbox', label: 'Inbox', icon: '📬', section: 'settings' },
      { id: 'admin', label: 'Admin Panel', icon: '🔧', section: 'settings' },
    ] : [])
  ];

  const mainItems = navItems.filter(item => item.section === 'main');
  const toolItems = navItems.filter(item => item.section === 'tools');
  const settingsItems = navItems.filter(item => item.section === 'settings');

  // User display helpers
  const getUserDisplayName = () => {
    return user?.user_metadata?.full_name ||
           user?.user_metadata?.name ||
           user?.email?.split('@')[0] ||
           'User';
  };

  const getUserAvatar = () => {
    return user?.user_metadata?.avatar_url ||
           user?.user_metadata?.picture ||
           null;
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    const parts = name.split(' ').filter(p => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavClick = (tabId) => {
    onTabChange(tabId);
    setShowMobileMenu(false);
  };

  const styles = {
    // Desktop Sidebar
    sidebar: {
      position: 'fixed',
      left: 0,
      top: 0,
      bottom: 0,
      width: isCollapsed ? '72px' : '240px',
      backgroundColor: '#FFFFFF',
      borderRight: '1px solid #E8E8E8',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s ease-in-out',
      zIndex: 1000,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    // Logo section
    logoSection: {
      padding: '20px 16px',
      borderBottom: '1px solid #E8E8E8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: isCollapsed ? 'center' : 'space-between',
      gap: '8px',
      minHeight: '72px',
    },
    logoWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
    },
    logoIcon: {
      width: '34px',
      height: '34px',
      borderRadius: '10px',
      flexShrink: 0,
      objectFit: 'cover',
    },
    logoText: {
      display: isCollapsed ? 'none' : 'block',
      overflow: 'hidden',
    },
    logoTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#111827',
      margin: 0,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      letterSpacing: '-0.025em',
    },
    logoSubtitle: {
      fontSize: '11px',
      color: '#A1A1AA',
      margin: 0,
      marginTop: '2px',
    },
    collapseBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      color: '#A1A1AA',
      borderRadius: '8px',
      flexShrink: 0,
      transition: 'color 0.15s ease',
    },
    // Navigation sections
    navSection: {
      flex: 1,
      padding: '16px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    },
    sectionLabel: {
      fontSize: '11px',
      fontWeight: '500',
      color: '#A1A1AA',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      padding: '8px 12px 6px',
      display: isCollapsed ? 'none' : 'block',
    },
    navItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: isCollapsed ? '10px' : '9px 12px',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      border: 'none',
      backgroundColor: 'transparent',
      width: '100%',
      textAlign: 'left',
      justifyContent: isCollapsed ? 'center' : 'flex-start',
    },
    navItemActive: {
      backgroundColor: 'rgba(59, 130, 246, 0.08)',
      color: '#3B82F6',
    },
    navItemHover: {
      backgroundColor: '#F5F5F5',
    },
    navIcon: {
      fontSize: '16px',
      width: '22px',
      textAlign: 'center',
      flexShrink: 0,
    },
    navLabel: {
      fontSize: '13.5px',
      fontWeight: '500',
      color: '#374151',
      whiteSpace: 'nowrap',
      display: isCollapsed ? 'none' : 'block',
      letterSpacing: '-0.011em',
    },
    navLabelActive: {
      color: '#3B82F6',
      fontWeight: '600',
    },
    // Status indicator
    statusSection: {
      padding: '12px 16px',
      borderTop: '1px solid #E8E8E8',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: isCollapsed ? 'center' : 'flex-start',
    },
    statusDot: {
      width: '7px',
      height: '7px',
      borderRadius: '50%',
      backgroundColor: serverStatus === 'online' ? '#10B981' : '#EF4444',
      flexShrink: 0,
    },
    statusText: {
      fontSize: '12px',
      color: serverStatus === 'online' ? '#10B981' : '#EF4444',
      display: isCollapsed ? 'none' : 'block',
      letterSpacing: '-0.011em',
    },
    // User section
    userSection: {
      padding: '16px',
      borderTop: '1px solid #E8E8E8',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      justifyContent: isCollapsed ? 'center' : 'flex-start',
    },
    avatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      objectFit: 'cover',
      border: '1.5px solid #E8E8E8',
      flexShrink: 0,
    },
    avatarFallback: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: '#3B82F6',
      color: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: '600',
      flexShrink: 0,
    },
    userInfo: {
      flex: 1,
      overflow: 'hidden',
      display: isCollapsed ? 'none' : 'block',
    },
    userName: {
      fontSize: '13.5px',
      fontWeight: '600',
      color: '#111827',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      letterSpacing: '-0.011em',
    },
    userEmail: {
      fontSize: '12px',
      color: '#A1A1AA',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    logoutBtn: {
      display: isCollapsed ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      color: '#EF4444',
      borderRadius: '8px',
      fontSize: '16px',
      flexShrink: 0,
      transition: 'background-color 0.15s ease',
    },
    // Mobile styles
    mobileHeader: {
      display: 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: '56px',
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid #E8E8E8',
      padding: '0 16px',
      alignItems: 'center',
      justifyContent: 'space-between',
      zIndex: 1001,
    },
    mobileMenuBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      fontSize: '24px',
      color: '#374151',
    },
    mobileOverlay: {
      display: showMobileMenu ? 'block' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      zIndex: 999,
    },
    mobileSidebar: {
      position: 'fixed',
      left: showMobileMenu ? 0 : '-280px',
      top: 0,
      bottom: 0,
      width: '280px',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      transition: 'left 0.3s ease-in-out',
      zIndex: 1002,
      overflowY: 'auto',
    },
    // Bottom navigation for mobile
    bottomNav: {
      display: 'none',
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
      backgroundColor: '#FFFFFF',
      borderTop: '1px solid #E8E8E8',
      justifyContent: 'space-around',
      alignItems: 'flex-start',
      padding: '8px 8px env(safe-area-inset-bottom, 0px)',
      zIndex: 1000,
    },
    bottomNavItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 12px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      color: '#A1A1AA',
      minWidth: '60px',
      transition: 'color 0.15s ease',
    },
    bottomNavItemActive: {
      color: '#3B82F6',
    },
    bottomNavIcon: {
      fontSize: '20px',
      marginBottom: '4px',
    },
    bottomNavLabel: {
      fontSize: '10px',
      fontWeight: '500',
      letterSpacing: '-0.011em',
    },
  };

  // Apply responsive styles via CSS media queries
  const responsiveStyles = `
    @media (max-width: 768px) {
      .desktop-sidebar { display: none !important; }
      .mobile-header { display: flex !important; }
      .bottom-nav { display: flex !important; }
    }
    @media (min-width: 769px) {
      .desktop-sidebar { display: flex !important; }
      .mobile-header { display: none !important; }
      .bottom-nav { display: none !important; }
      .mobile-overlay { display: none !important; }
      .mobile-sidebar { display: none !important; }
    }
  `;

  const renderNavItem = (item, index) => {
    const isActive = activeTab === item.id;
    return (
      <button
        key={item.id}
        onClick={() => handleNavClick(item.id)}
        style={{
          ...styles.navItem,
          ...(isActive ? styles.navItemActive : {}),
        }}
        onMouseEnter={(e) => {
          if (!isActive) e.currentTarget.style.backgroundColor = '#F5F5F5';
        }}
        onMouseLeave={(e) => {
          if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
        }}
        title={isCollapsed ? item.label : undefined}
      >
        <span style={styles.navIcon}>{item.icon}</span>
        <span style={{
          ...styles.navLabel,
          ...(isActive ? styles.navLabelActive : {}),
        }}>{item.label}</span>
      </button>
    );
  };

  // Bottom nav items (limited for mobile)
  const bottomNavItems = [
    { id: 'expenses', label: 'Expenses', icon: '💰' },
    { id: 'upload', label: 'Upload', icon: '📸' },
    { id: 'analytics', label: 'Analytics', icon: '📊' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  return (
    <>
      <style>{responsiveStyles}</style>

      {/* Desktop Sidebar */}
      <aside className="desktop-sidebar" style={styles.sidebar}>
        {/* Logo */}
        <div style={styles.logoSection}>
          <div style={styles.logoWrapper}>
            <img src={process.env.PUBLIC_URL + '/zenith-logo.png'} alt="Zenith" style={styles.logoIcon} />
            <div style={styles.logoText}>
              <h1 style={styles.logoTitle}>Zenith Expense</h1>
              <p style={styles.logoSubtitle}>Expense Tracker</p>
            </div>
          </div>
          <button
            style={styles.collapseBtn}
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '→' : '←'}
          </button>
        </div>

        {/* Navigation */}
        <nav style={styles.navSection}>
          {!isCollapsed && <div style={styles.sectionLabel}>Main</div>}
          {mainItems.map(renderNavItem)}

          {!isCollapsed && <div style={{ ...styles.sectionLabel, marginTop: '16px' }}>Tools</div>}
          {toolItems.map(renderNavItem)}

          {!isCollapsed && <div style={{ ...styles.sectionLabel, marginTop: '16px' }}>Settings</div>}
          {settingsItems.map(renderNavItem)}
        </nav>

        {/* Status */}
        <div style={styles.statusSection}>
          <div style={styles.statusDot}></div>
          <span style={styles.statusText}>
            {serverStatus === 'online' ? 'Connected' : 'Offline'}
          </span>
        </div>

        {/* User */}
        <div style={styles.userSection}>
          {getUserAvatar() && !avatarError ? (
            <img
              src={getUserAvatar()}
              alt={getUserDisplayName()}
              style={styles.avatar}
              onError={() => setAvatarError(true)}
            />
          ) : (
            <div style={styles.avatarFallback}>{getUserInitials()}</div>
          )}
          <div style={styles.userInfo}>
            <div style={styles.userName}>{getUserDisplayName()}</div>
            <div style={styles.userEmail}>{user?.email}</div>
          </div>
          <button
            style={styles.logoutBtn}
            onClick={handleLogout}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            title="Logout"
          >
            🚪
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="mobile-header" style={styles.mobileHeader}>
        <button style={styles.mobileMenuBtn} onClick={() => setShowMobileMenu(true)}>
          ☰
        </button>
        <h1 style={{ fontSize: '16px', fontWeight: '600', margin: 0, color: '#111827', letterSpacing: '-0.025em' }}>
          Zenith Expense
        </h1>
        <div style={{ width: '40px' }}></div>
      </header>

      {/* Mobile Overlay */}
      <div
        className="mobile-overlay"
        style={styles.mobileOverlay}
        onClick={() => setShowMobileMenu(false)}
      ></div>

      {/* Mobile Sidebar */}
      <aside className="mobile-sidebar" style={styles.mobileSidebar}>
        {/* Logo */}
        <div style={{ ...styles.logoSection, justifyContent: 'space-between' }}>
          <div style={styles.logoWrapper}>
            <img src={process.env.PUBLIC_URL + '/zenith-logo.png'} alt="Zenith" style={styles.logoIcon} />
            <div>
              <h1 style={styles.logoTitle}>Zenith Expense</h1>
              <p style={styles.logoSubtitle}>Expense Tracker</p>
            </div>
          </div>
          <button
            style={styles.mobileMenuBtn}
            onClick={() => setShowMobileMenu(false)}
          >
            ✕
          </button>
        </div>

        {/* Navigation */}
        <nav style={{ ...styles.navSection, display: 'flex' }}>
          <div style={{ ...styles.sectionLabel, display: 'block' }}>Main</div>
          {mainItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              style={{
                ...styles.navItem,
                justifyContent: 'flex-start',
                ...(activeTab === item.id ? styles.navItemActive : {}),
              }}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              <span style={{
                ...styles.navLabel,
                display: 'block',
                ...(activeTab === item.id ? styles.navLabelActive : {}),
              }}>{item.label}</span>
            </button>
          ))}

          <div style={{ ...styles.sectionLabel, display: 'block', marginTop: '16px' }}>Tools</div>
          {toolItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              style={{
                ...styles.navItem,
                justifyContent: 'flex-start',
                ...(activeTab === item.id ? styles.navItemActive : {}),
              }}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              <span style={{
                ...styles.navLabel,
                display: 'block',
                ...(activeTab === item.id ? styles.navLabelActive : {}),
              }}>{item.label}</span>
            </button>
          ))}

          <div style={{ ...styles.sectionLabel, display: 'block', marginTop: '16px' }}>Settings</div>
          {settingsItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              style={{
                ...styles.navItem,
                justifyContent: 'flex-start',
                ...(activeTab === item.id ? styles.navItemActive : {}),
              }}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              <span style={{
                ...styles.navLabel,
                display: 'block',
                ...(activeTab === item.id ? styles.navLabelActive : {}),
              }}>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* User section in mobile */}
        <div style={{ ...styles.userSection, marginTop: 'auto' }}>
          {getUserAvatar() && !avatarError ? (
            <img
              src={getUserAvatar()}
              alt={getUserDisplayName()}
              style={styles.avatar}
              onError={() => setAvatarError(true)}
            />
          ) : (
            <div style={styles.avatarFallback}>{getUserInitials()}</div>
          )}
          <div style={{ ...styles.userInfo, display: 'block' }}>
            <div style={styles.userName}>{getUserDisplayName()}</div>
            <div style={styles.userEmail}>{user?.email}</div>
          </div>
          <button
            style={{ ...styles.logoutBtn, display: 'flex' }}
            onClick={handleLogout}
            title="Logout"
          >
            🚪
          </button>
        </div>
      </aside>

      {/* Bottom Navigation (Mobile) */}
      <nav className="bottom-nav" style={styles.bottomNav}>
        {bottomNavItems.map(item => (
          <button
            key={item.id}
            onClick={() => handleNavClick(item.id)}
            style={{
              ...styles.bottomNavItem,
              ...(activeTab === item.id ? styles.bottomNavItemActive : {}),
            }}
          >
            <span style={styles.bottomNavIcon}>{item.icon}</span>
            <span style={styles.bottomNavLabel}>{item.label}</span>
          </button>
        ))}
        <button
          style={styles.bottomNavItem}
          onClick={() => setShowMobileMenu(true)}
        >
          <span style={styles.bottomNavIcon}>☰</span>
          <span style={styles.bottomNavLabel}>More</span>
        </button>
      </nav>
    </>
  );
}

export default Sidebar;
